/**
 * <burnout-carousel>
 *
 * Arrows and a progress bar for a native scroll-snap rail.
 *
 * The scrolling itself is CSS `scroll-snap-type` — no JS drives position, so
 * touch, trackpad, keyboard and momentum all behave exactly as the platform
 * intends. This only adds the desktop affordances on top.
 *
 * Expects, anywhere inside:
 *   .bo-rail            the scroll container
 *   [data-bo-rail-prev] [data-bo-rail-next]   optional buttons
 *   .bo-rail__progress-bar                    optional progress indicator
 */
class BurnoutCarousel extends HTMLElement {
  #rail = null;
  #prev = null;
  #next = null;
  #bar = null;
  #ticking = false;

  connectedCallback() {
    this.#rail = this.querySelector('.bo-rail');
    if (!this.#rail) return;

    this.#prev = this.querySelector('[data-bo-rail-prev]');
    this.#next = this.querySelector('[data-bo-rail-next]');
    this.#bar = this.querySelector('.bo-rail__progress-bar');

    this.#prev?.addEventListener('click', () => this.#scrollByPage(-1));
    this.#next?.addEventListener('click', () => this.#scrollByPage(1));

    this.#rail.addEventListener('scroll', this.#onScroll, { passive: true });

    // Card widths change with the viewport and once fonts land, so re-measure
    // rather than caching geometry from first paint.
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => this.#update()).observe(this.#rail);
    } else {
      window.addEventListener('resize', () => this.#update());
    }

    this.#update();
  }

  /** Advance by one card, falling back to most of a viewport width. */
  #scrollByPage = (direction) => {
    const card = this.#rail.querySelector(':scope > *');
    const gap = parseFloat(getComputedStyle(this.#rail).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.#rail.clientWidth * 0.8;
    this.#rail.scrollBy({ left: step * direction, behavior: 'smooth' });
  };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#update();
      this.#ticking = false;
    });
  };

  #update = () => {
    const max = this.#rail.scrollWidth - this.#rail.clientWidth;
    // A rail whose content fits needs no controls at all.
    const overflows = max > 4;
    this.toggleAttribute('data-overflows', overflows);

    if (this.#prev) this.#prev.disabled = !overflows || this.#rail.scrollLeft <= 2;
    if (this.#next) this.#next.disabled = !overflows || this.#rail.scrollLeft >= max - 2;

    if (this.#bar) {
      const ratio = overflows ? this.#rail.scrollLeft / max : 0;
      // Bar length reflects how much of the rail is visible; position reflects
      // how far through it you are.
      const visible = overflows ? this.#rail.clientWidth / this.#rail.scrollWidth : 1;
      this.#bar.style.setProperty('--bo-rail-visible', visible);
      this.#bar.style.setProperty('--bo-rail-progress', ratio);
    }
  };
}

if (!customElements.get('burnout-carousel')) {
  customElements.define('burnout-carousel', BurnoutCarousel);
}
